import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowRight,
  Phone,
  Check,
  Star,
  Shield,
  Cloud,
  Monitor,
  Server,
  Award,
  Building2,
  ThumbsUp,
  Clock,
  MapPin,
  ChevronRight,
  LayoutGrid,
  MailPlus,
  Users,
  ShieldCheck,
  Database,
  Network,
  Lightbulb,
  Stethoscope,
  Scale,
  Calculator,
  Briefcase,
  MessageSquare,
  TrendingUp,
  BadgeDollarSign,
  Headphones,
  Settings,
  Flag,
  Unlock,
  Zap,
  FileText,
} from "lucide-react";
import {
  companyInfo,
  services,
  industries,
  stats,
  whyChooseReasons,
  faqItems,
  trustSignals,
} from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const serviceIconMap = {
  Monitor, LayoutGrid, MailPlus, Server, Users, ShieldCheck,
  DatabaseBackup: Database, Cloud, Network, Lightbulb,
};

const industryIconMap = {
  Stethoscope, Scale, Building2, Calculator, Briefcase,
};

const whyIconMap = {
  Award, MapPin, MessageSquare, TrendingUp, BadgeDollarSign, Shield,
};

const trustIconMap = {
  Flag, Award, Unlock, Star, Zap,
};

const Section = ({ children, className = "", id }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      id={id}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

/* ─────────────────────── HERO ─────────────────────── */
const HeroSection = () => (
  <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white">
    <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-3xl opacity-50" />
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-50 rounded-full blur-3xl opacity-40" />

    <div className="container mx-auto px-6 py-20 lg:py-28 relative">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div className="page-enter">
          <Badge
            variant="secondary"
            className="mb-6 px-4 py-1.5 text-blue-700 bg-blue-50 border border-blue-100 text-sm font-medium"
          >
            Australian-Owned IT Support Company
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-slate-900 leading-[1.1] tracking-tight mb-5">
            {companyInfo.heroHeadline}
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed mb-7 max-w-xl">
            {companyInfo.heroSubheadline}
          </p>

          {/* Trust checkmarks */}
          <div className="space-y-3 mb-8">
            {companyInfo.heroChecks.map((check, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-blue-700" />
                </div>
                <span className="text-base font-medium text-slate-700">
                  {check}
                </span>
              </div>
            ))}
          </div>

          {/* 3 CTAs */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              asChild
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-7 text-base"
            >
              <Link to="/contact">
                Get a Free Consultation
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 px-7 text-base border-slate-300 text-slate-800 hover:bg-slate-50 font-semibold"
            >
              <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                <Phone className="w-4 h-4 mr-2 text-blue-600" />
                Call Now
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 px-7 text-base border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Link to="/contact">
                <FileText className="w-4 h-4 mr-2 text-blue-600" />
                Request a Quote
              </Link>
            </Button>
          </div>
          <p className="text-sm text-slate-500">
            Or call us directly:{" "}
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
              className="font-semibold text-slate-700 hover:text-blue-600 transition-colors"
            >
              {companyInfo.phone}
            </a>
          </p>
        </div>

        {/* Right — decorative floating cards + experience badge */}
        <div className="hidden lg:block relative">
          <div className="grid grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, label: "Cybersecurity", desc: "Multi-layer protection" },
              { icon: Cloud, label: "Cloud & Azure", desc: "Migration & management" },
              { icon: Headphones, label: "IT Helpdesk", desc: "Remote & on-site help" },
              { icon: Settings, label: "Infrastructure", desc: "Servers & networks" },
            ].map((item, i) => (
              <div
                key={i}
                className={`float-card p-6 bg-white rounded-2xl shadow-md border border-slate-100/80 flex flex-col items-center gap-2 text-center ${
                  i % 2 === 0 ? "mt-8" : "mb-8"
                }`}
              >
                <div className="w-[52px] h-[52px] rounded-xl bg-blue-50 flex items-center justify-center mb-1">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-slate-800">
                  {item.label}
                </span>
                <span className="text-xs text-slate-500">{item.desc}</span>
              </div>
            ))}
          </div>

          {/* Experience badge */}
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg border border-slate-100 px-6 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900 leading-tight">
                20+ Years
              </div>
              <div className="text-xs text-slate-500">Trusted Experience</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ──────────────────── TRUST SIGNALS BAR ──────────────────── */
const TrustBar = () => (
  <div className="bg-white border-b border-slate-100 py-5 overflow-hidden">
    <div className="container mx-auto px-6">
      <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
        {trustSignals.map((signal, i) => {
          const Icon = trustIconMap[signal.icon] || Award;
          return (
            <div
              key={i}
              className="flex items-center gap-2 text-sm text-slate-600"
            >
              <Icon className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="font-medium whitespace-nowrap">{signal.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ──────────────────── STATS ──────────────────── */
const StatsSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const statIcons = { Award, Building2, ThumbsUp, Clock };

  return (
    <div
      ref={ref}
      className={`bg-slate-900 relative grain-overlay stagger-children ${
        isVisible ? "visible" : ""
      }`}
    >
      <div className="container mx-auto px-6 py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {stats.map((stat, i) => {
            const Icon = statIcons[stat.icon] || Award;
            return (
              <div key={i} className="text-center">
                <Icon className="w-6 h-6 text-blue-400 mx-auto mb-3" />
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400 font-medium">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────── WHAT WE DO ──────────────────── */
const WhatWeDoSection = () => (
  <Section className="py-20 lg:py-28 bg-white">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Badge
            variant="secondary"
            className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
          >
            What We Do
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-5">
            Your Trusted IT Partner
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {companyInfo.whatWeDo}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {companyInfo.whatWeDoPoints.map((point, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-5 rounded-xl bg-slate-50 border border-slate-100"
            >
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                <Check className="w-3.5 h-3.5 text-blue-700" />
              </div>
              <span className="text-slate-700 leading-relaxed text-[0.95rem]">
                {point}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

/* ──────────────────── CORE SERVICES ──────────────────── */
const ServicesSection = () => (
  <Section className="py-20 lg:py-28 bg-slate-50">
    <div className="container mx-auto px-6">
      <div className="max-w-2xl mb-14">
        <Badge
          variant="secondary"
          className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
        >
          Our Core Services
        </Badge>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
          Managed IT Services & Technical Support
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed">
          Everything your business needs to stay connected, secure, and
          productive — explained in plain English, managed by local experts.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.slice(0, 6).map((service) => {
          const Icon = serviceIconMap[service.icon] || Monitor;
          return (
            <Link key={service.id} to={`/services/${service.id}`}>
              <Card className="service-card h-full border-slate-200/60 hover:border-blue-100 bg-white group cursor-pointer">
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2.5">
                    {service.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">
                    {service.shortDesc}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 group-hover:gap-2.5 transition-all duration-200">
                    Learn more
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <Button
          asChild
          variant="outline"
          size="lg"
          className="border-slate-300 text-slate-700 hover:bg-white"
        >
          <Link to="/services">
            View All 10 Services
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  </Section>
);

/* ──────────────────── INDUSTRIES ──────────────────── */
const IndustriesSection = () => (
  <Section className="py-20 lg:py-28 bg-white">
    <div className="container mx-auto px-6">
      <div className="max-w-3xl mb-14">
        <Badge
          variant="secondary"
          className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
        >
          Industries We Serve
        </Badge>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
          Specialist IT Support for Regulated &amp; Professional Industries
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed">
          We specialise in supporting businesses that rely on secure, compliant,
          and always-on IT systems. Our deep industry experience means we
          understand your unique challenges from day one.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {industries.map((ind) => {
          const Icon = industryIconMap[ind.icon] || Briefcase;
          return (
            <Link key={ind.id} to={`/industries/${ind.id}`}>
              <Card className="service-card h-full border-slate-100 bg-white group cursor-pointer hover:border-blue-100">
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-5 group-hover:bg-blue-50 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-slate-600 group-hover:text-blue-600 transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    IT Support for {ind.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">
                    {ind.shortDesc}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 group-hover:gap-2.5 transition-all duration-200">
                    Learn more
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  </Section>
);

/* ──────────────────── WHY CHOOSE ──────────────────── */
const WhySection = () => (
  <Section className="py-20 lg:py-28 bg-slate-50">
    <div className="container mx-auto px-6">
      <div className="max-w-2xl mx-auto text-center mb-14">
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
          Why Australian Businesses Choose FlexiTech
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed">
          We're not just another IT company. Here's what makes FlexiTech the
          preferred IT partner for over 500 Australian businesses.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {whyChooseReasons.map((reason, i) => {
          const Icon = whyIconMap[reason.icon] || Award;
          return (
            <div
              key={i}
              className="p-7 rounded-2xl bg-white border border-slate-100 hover:shadow-md transition-shadow duration-300"
            >
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {reason.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                {reason.description}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <Button
          asChild
          variant="outline"
          size="lg"
          className="border-slate-300 text-slate-700 hover:bg-white"
        >
          <Link to="/why-flexitech">
            See the Full Story
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  </Section>
);

/* ──────────────────── FAQ ──────────────────── */
const FAQSection = () => (
  <Section className="py-20 lg:py-28 bg-slate-50">
    <div className="container mx-auto px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Got questions? We've got answers.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {faqItems.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border border-slate-200/80 rounded-xl px-6 bg-white data-[state=open]:border-blue-100 data-[state=open]:bg-blue-50/30 transition-colors"
            >
              <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline py-5 text-[0.95rem]">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5 text-sm">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </Section>
);

/* ──────────────────── BOTTOM CTA ──────────────────── */
const CTASection = () => (
  <Section className="py-20 lg:py-24 bg-slate-900 relative grain-overlay">
    <div className="container mx-auto px-6 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-5">
          Ready to Take the Stress Out of IT?
        </h2>
        <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
          Let's have a no-obligation conversation about how FlexiTech can make
          technology work for your business — not against it.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-4">
          <Button
            asChild
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8 text-base"
          >
            <Link to="/contact">
              Book a Free Consultation
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="h-12 px-8 text-base border-slate-600 text-white hover:bg-slate-800 hover:text-white"
          >
            <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
              <Phone className="w-4 h-4 mr-2" />
              Call {companyInfo.phone}
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="h-12 px-8 text-base border-slate-600 text-white hover:bg-slate-800 hover:text-white"
          >
            <Link to="/contact">
              <FileText className="w-4 h-4 mr-2" />
              Request a Quote
            </Link>
          </Button>
        </div>
        <p className="text-slate-400 text-sm">
          No lock-in contracts. No hidden fees. Just honest, reliable IT support.
        </p>
      </div>
    </div>
  </Section>
);

/* ──────────────────── PAGE ──────────────────── */
export default function Home() {
  return (
    <div>
      <HeroSection />
      <TrustBar />
      <StatsSection />
      <WhatWeDoSection />
      <ServicesSection />
      <IndustriesSection />
      <WhySection />
      <FAQSection />
      <CTASection />
    </div>
  );
}
